import { AlertTriangle, Search, Settings2 } from "lucide-react";
import { useRefresh } from "ra-core";
import { useState } from "react";
import { useSearchParams } from "react-router";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

import {
  PAYMENT_METHODS,
  PRODUCTS,
  formatEuro,
  formatLongDate,
  labelOf,
} from "../gulaConstants";
import { fullName } from "../gulaTypes";
import { CostSettingsDialog } from "./CostSettingsDialog";
import { MoneyTabs } from "./MoneyTabs";
import { ProfitSheet } from "./ProfitSheet";
import { useProfitRows, type ProfitRow } from "./useMoneyData";

const round2 = (value: number) => Math.round(value * 100) / 100;

const costsOf = (row: ProfitRow) =>
  round2(
    row.result.directCosts +
      row.result.saleFee +
      row.result.commission +
      row.result.recurringFeesToDate +
      row.result.recurringCostsToDate,
  );

const Summary = ({
  label,
  value,
  hint,
  negative,
}: {
  label: string;
  value: string;
  hint?: string;
  negative?: boolean;
}) => (
  <div className="rounded-lg border bg-card p-4">
    <div className="text-sm text-muted-foreground">{label}</div>
    <div
      className={cn(
        "mt-1 text-2xl font-semibold tabular-nums",
        negative && "text-destructive",
      )}
    >
      {value}
    </div>
    {hint ? (
      <div className="mt-1 text-xs text-muted-foreground">{hint}</div>
    ) : null}
  </div>
);

/** Profit of every sale, one client at a time. */
export const ProfitPage = () => {
  const { rows, settings, isPending } = useProfitRows();
  const refresh = useRefresh();
  const [searchParams, setSearchParams] = useSearchParams();
  const [query, setQuery] = useState("");
  const [onlyPending, setOnlyPending] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const openId = searchParams.get("pedido");

  const openRow = rows.find((row) => String(row.order.id) === openId);
  const setOpen = (id?: string) => {
    const next = new URLSearchParams(searchParams);
    if (id) next.set("pedido", id);
    else next.delete("pedido");
    setSearchParams(next, { replace: true });
  };

  const needle = query.trim().toLowerCase();
  const visible = rows.filter(
    (row) =>
      (!onlyPending || !row.record) &&
      (!needle ||
        [fullName(row.order), row.order.city, row.order.installer_name]
          .filter(Boolean)
          .join(" ")
          .toLowerCase()
          .includes(needle)),
  );

  const totalProfit = round2(
    rows.reduce((sum, row) => sum + row.result.profitToDate, 0),
  );
  const totalRevenue = rows.reduce(
    (sum, row) => sum + row.result.revenueNetToDate,
    0,
  );
  const monthly = round2(
    rows.reduce((sum, row) => sum + row.result.recurringProfitMonth, 0),
  );
  const reviewed = rows.filter((row) => row.record).length;

  return (
    <div className="flex flex-col gap-6 py-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Beneficio por cliente</h1>
          <p className="text-sm text-muted-foreground">
            Lo que deja cada venta después de IVA, comisiones y costes. Abre un
            cliente para meter sus costes reales.
          </p>
        </div>
        <MoneyTabs />
      </div>

      {isPending || !settings ? (
        <p className="text-sm text-muted-foreground">Cargando ventas…</p>
      ) : (
        <>
          {!Number(settings.equipment_cost) ? (
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-100">
              <span className="flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden />
                Faltan los costes por defecto (equipo, instalador, envío): de
                momento el beneficio solo descuenta IVA y comisiones.
              </span>
              <Button size="sm" onClick={() => setShowSettings(true)}>
                Rellenar costes
              </Button>
            </div>
          ) : null}

          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Summary
              label="Beneficio a hoy"
              value={formatEuro(totalProfit)}
              hint={`${rows.length} ventas`}
              negative={totalProfit < 0}
            />
            <Summary
              label="Margen medio"
              value={
                totalRevenue > 0
                  ? `${(Math.round((totalProfit / totalRevenue) * 1000) / 10).toLocaleString("es-ES")} %`
                  : "–"
              }
              hint="sobre lo cobrado sin IVA"
            />
            <Summary
              label="Las cuotas suman cada mes"
              value={formatEuro(monthly)}
              hint="de beneficio, ya descontados filtros y comisiones"
            />
            <Summary
              label="Ventas revisadas"
              value={`${reviewed} de ${rows.length}`}
              hint="El resto usa los costes por defecto"
            />
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <div className="relative w-full sm:w-64">
              <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Buscar cliente, ciudad, instalador"
                className="pl-8"
              />
            </div>
            <Button
              variant={onlyPending ? "default" : "outline"}
              size="sm"
              onClick={() => setOnlyPending((value) => !value)}
            >
              Sin revisar
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="ml-auto"
              onClick={() => setShowSettings(true)}
            >
              <Settings2 className="h-4 w-4" />
              Costes por defecto
            </Button>
          </div>

          <div className="overflow-x-auto rounded-lg border">
            <table className="w-full min-w-[760px] text-sm">
              <thead className="bg-muted/50 text-left text-xs text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 font-medium">Cliente</th>
                  <th className="px-3 py-2 font-medium">Venta</th>
                  <th className="px-3 py-2 text-right font-medium">
                    Cobrado sin IVA
                  </th>
                  <th className="px-3 py-2 text-right font-medium">Costes</th>
                  <th className="px-3 py-2 text-right font-medium">
                    Beneficio a hoy
                  </th>
                  <th className="px-3 py-2 text-right font-medium">Cada mes</th>
                  <th className="px-3 py-2 font-medium">Estado</th>
                </tr>
              </thead>
              <tbody className="tabular-nums">
                {visible.map((row) => (
                  <tr
                    key={String(row.order.id)}
                    className="cursor-pointer border-t hover:bg-muted/40"
                    onClick={() => setOpen(String(row.order.id))}
                  >
                    <td className="px-3 py-2">
                      <div className="font-medium">{fullName(row.order)}</div>
                      <div className="text-xs text-muted-foreground">
                        {formatLongDate(
                          row.order.paid_at ?? row.order.created_at,
                        )}
                        {row.order.city ? ` · ${row.order.city}` : ""}
                      </div>
                    </td>
                    <td className="px-3 py-2">
                      <div>{labelOf(PRODUCTS, row.order.product)}</div>
                      <div className="text-xs text-muted-foreground">
                        {labelOf(PAYMENT_METHODS, row.order.payment_method)}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-right">
                      {formatEuro(row.result.revenueNetToDate)}
                    </td>
                    <td className="px-3 py-2 text-right">
                      {formatEuro(costsOf(row))}
                    </td>
                    <td
                      className={cn(
                        "px-3 py-2 text-right font-semibold",
                        row.result.profitToDate < 0 && "text-destructive",
                      )}
                    >
                      {formatEuro(row.result.profitToDate)}
                      {row.result.marginPct != null ? (
                        <div className="text-xs font-normal text-muted-foreground">
                          {row.result.marginPct.toLocaleString("es-ES")} %
                        </div>
                      ) : null}
                    </td>
                    <td className="px-3 py-2 text-right">
                      {row.result.recurringProfitMonth
                        ? formatEuro(row.result.recurringProfitMonth)
                        : "–"}
                    </td>
                    <td className="px-3 py-2">
                      <Badge variant={row.record ? "default" : "secondary"}>
                        {row.record ? "Revisado" : "Estimado"}
                      </Badge>
                    </td>
                  </tr>
                ))}
                {!visible.length ? (
                  <tr>
                    <td
                      colSpan={7}
                      className="px-3 py-6 text-center text-muted-foreground"
                    >
                      No hay ventas que coincidan.
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>

          {openRow ? (
            <ProfitSheet
              row={openRow}
              settings={settings}
              onClose={() => setOpen()}
              onSaved={refresh}
            />
          ) : null}
          {showSettings ? (
            <CostSettingsDialog
              settings={settings}
              onClose={() => setShowSettings(false)}
              onSaved={() => {
                setShowSettings(false);
                refresh();
              }}
            />
          ) : null}
        </>
      )}
    </div>
  );
};

ProfitPage.path = "/dinero/beneficio";
